/* frontend/src/pages/styleGuide/StyleGuideContent.js */

export const STYLE_GUIDE_LAST_UPDATED = "2024-09-15";

/**
 * Your renderer is treating every section `title` the same (likely as an H2).
 * Fix: only use `title` for true H2 sections, and render H3/H4 as blocks
 * inside the parent section using block types like "h3" / "h4".
 *
 * This assumes your block renderer supports heading block types.
 */
export const STYLE_GUIDE_SECTIONS = [
  {
    title: "Mission and values",
    blocks: [
      {
        type: "p",
        text: "For Love of Writers (FLoW) aims to provide our readers with content that engages, uplifts, and inspires. We strive to deliver articles that empower, inspire, and enrich the lives of our readers and help them lead happier, healthier, and more fulfilling lives.",
      },
      {
        type: "p",
        text: "We value diversity, inclusivity, and respect for all individuals. We are committed to providing a safe and welcoming environment for our readers and contributors, regardless of their gender, race, ethnicity, religion, sexual orientation, or any other personal characteristic.",
      },
      {
        type: "p",
        text: "We also value accuracy, transparency, and accountability. We ensure all our content is fact-checked and verified before publication, and we are committed to promptly correcting any errors or inaccuracies. We also maintain transparency with our readers about our sources and editorial process.",
      },
      {
        type: "p",
        text: "In addition, we value creativity, innovation, and continuous improvement. We encourage our contributors to think outside the box and explore new and innovative storytelling and content creation approaches. We also regularly review and update our style guide to ensure we continue to improve and deliver the best possible content to our readers.",
      },
    ],
  },

  {
    title: "Audience",
    blocks: [
      {
        type: "p",
        text: "At FLoW, we are dedicated to providing content that inspires and empowers our readers. Our diverse audience members come from all walks of life and share a common interest in entertaining and thought-provoking content.",
      },
      {
        type: "p",
        text: "Regardless of our readers' educational backgrounds or personal interests, we strive to provide content that is accessible and meaningful. Above all, we aim to deliver a positive and uplifting experience for our readers. We want to create a sense of community that connects people worldwide by providing a platform that encourages and empowers our audience. Whether through our articles, videos, or social media channels, we are committed to delivering content that enriches the lives of our readers in meaningful and lasting ways.",
      },
    ],
  },

  {
    title: "Language and style",
    blocks: [
      {
        type: "p",
        text: 'To achieve our overall mission, we recommend that our writers avoid using negative words and language such as "no," "not," "can\'t," "don\'t," "won\'t," "but," "however," "unfortunately," and other similar words and instead focus on using positive and affirmative language to encourage and empower our readers.',
      },
      {
        type: "p",
        text: "Additionally, we encourage the use of active voice in our writing. This not only makes our writing more engaging and impactful, but it also helps to keep our content concise and easy to read. By using active voice, we can emphasize the sentence’s subject and make our message clearer and more direct.",
      },
      {
        type: "p",
        text: "While we understand that some topics require technical jargon or specialized terms to explain complex concepts, we encourage our writers to use jargon sparingly and only when necessary. When using technical terms, we recommend that our writers provide clear definitions and explanations to help our readers understand the topic.",
      },

      { type: "h3", text: "Tone and voice" },
      {
        type: "p",
        text: "We want our readers to feel good after reading our content. Our voice should reflect that and be friendly, relatable, and conversational—like chatting with a close friend. By establishing a personal connection with our readers, we aim to create a sense of community that encourages engagement and feedback.",
      },
      {
        type: "p",
        text: "To ensure our content is informative and entertaining, we strive to make our writing engaging, lively, and fun to read. By using clear, concise language and avoiding technical jargon, we aim to make our content accessible and enjoyable to many types of readers.",
      },

      { type: "h3", text: "Point of view (POV)" },
      {
        type: "p",
        text: "The point of view (POV) used in our writing can greatly affect how our readers engage with our content. We aim to use the right POV with the right content.",
      },
      {
        type: "p",
        text: "First-person POV (I, we) can be used in opinion pieces, personal stories, or when the writer is part of the story. This POV allows the writer to share their thoughts, feelings, and experiences directly with the reader. However, we should use first-person POV sparingly to avoid overuse and ensure our content remains relatable to a wider audience.",
      },
      {
        type: "p",
        text: "Second-person POV (you) can engage the reader and create a sense of intimacy. While this is effective in instructional pieces and advice articles and when the writer wants to address the reader directly, it's important to avoid overusing second-person POV and instead find a balance between direct engagement and maintaining a broader perspective.",
      },
      {
        type: "p",
        text: "Third-person POV (he, she, they) can be used for news articles, informational pieces, and objective reporting. While this allows the writer to remain impartial and focus on presenting the facts clearly and concisely, it's important to avoid creating distance between the reader and the content. We should maintain an engaging and relatable tone even when using this POV.",
      },
      {
        type: "p",
        text: "Overall, the POV should be appropriate for the content and help engage the reader in an informative and entertaining way.",
      },

      { type: "h3", text: "Perspective" },
      {
        type: "p",
        text: "While often confused for POV, perspective represents how writers and fictional characters perceive the world around them. Perspective is influenced by many things, including personal experiences, cultural heritage, race, ethnicity, gender, sexual orientation, age, religion, education, profession, family, location, and physical and mental ability.",
      },
      {
        type: "p",
        text: "We actively encourage content from various perspectives as part of our dedication to creating an inclusive and welcoming environment.",
      },
    ],
  },

  {
    title: "Content length",
    blocks: [
      {
        type: "p",
        text: "Our articles should fall within the range of 500–3,000 words. This length gives us enough space to educate and engage our readers while ensuring the content is not overwhelming or too time-consuming.",
      },
      {
        type: "p",
        text: "We understand that some topics require more in-depth coverage. We encourage our writers to explore these topics to their fullest extent while ensuring our readers can consume this information in a manageable way. As such, we recommend that longer articles be divided into multi-part series, with each installment containing 500–3,000 words.",
      },
      {
        type: "p",
        text: "Our minimum publishing requirement for each writer is 2,500 words every 30 days. Writers can meet this minimum requirement in several different ways:",
      },
      {
        type: "ul",
        items: [
          "One article of at least 2,500 words.",
          "One article of 2,000–2,499 words plus one article of 500–999 words.",
          "One article of 1,500–1,999 words plus one article of 1,000–1,499 words.",
          "One article of 1,500–1,999 words plus two articles of 500–999 words.",
          "Two articles of 1,000–1,499 words plus one article of 500–999 words.",
          "One article of 1,000–1,499 words plus three articles of 500–999 words.",
          "Five articles of 500–999 words.",
        ],
      },
      {
        type: "p",
        text: "This structure allows for flexibility in how writers meet their monthly word count while ensuring everyone contributes equally to our publication's success.",
      },
    ],
  },

  {
    title: "Spelling, grammar, and punctuation",
    blocks: [
      {
        type: "p",
        text: "We strive to maintain high standards by adhering to Canadian English rules. Proper spelling, grammar, and punctuation are essential for delivering clear, concise, and effective content.",
      },
      {
        type: "p",
        text: "To achieve this, we recommend using the Oxford comma in all cases—except when it would create confusion—and encourage strong word choices and engaging writing to convey enthusiasm and excitement, rather than relying on excessive exclamation points. Overusing exclamation points can be distracting and may make our writing appear less professional.",
      },

      { type: "h3", text: "Canadian spelling conventions" },
      {
        type: "p",
        text: "Writers are advised to set their word processing programs to Canadian English to help with most spelling issues. They should also use the Canadian Oxford Dictionary—in print or online—as the final authority on proper Canadian spelling on anything not specifically covered in this guide.",
      },
      {
        type: "ul",
        items: [
          'Words that end with "our" instead of "or", e.g. colour, behaviour, favour, neighbour.',
          'Words that end with "ize" instead of "ise", e.g. standardize, organize, authorize.',
          'Words that end with "ence" instead of "ense", e.g. defence, licence.',
          'Words that end with "re" instead of "er", e.g. centre, litre, fibre, calibre.',
        ],
      },

      { type: "h3", text: "Punctuation" },

      { type: "h4", text: "Apostrophe" },
      {
        type: "ul",
        items: [
          "Use an apostrophe and an s to form the possessive of words ending in s if the second s is pronounced, e.g. the Jones’s apartment.",
          "If a second s would be silent, add only the apostrophe, e.g. the puppies’ tails.",
          'If a word is “plural in form, singular in meaning,” add only the apostrophe, e.g. politics’ true meaning, this species’ first record.¹',
        ],
      },

      { type: "h4", text: "Ellipsis" },
      {
        type: "ul",
        items: [
          "Use an ellipsis to represent a slow trailing off or missing word(s), particularly in the case of a quotation.",
          'Use “. . .” with a space before and a space after rather than “...”, e.g. If I didn’t know better, I’d almost think . . . but no, it couldn’t be.',
        ],
      },

      { type: "h4", text: "Em dash" },
      {
        type: "ul",
        items: [
          "Use an em dash to represent a sudden interruption (often in dialogue) or to set off a supporting phrase from the main part of a sentence.",
          'Use “—” with no spaces before or after rather than “--”, e.g. “The chancellor—he had been awake half the night—came down in an angry mood.”²',
        ],
      },

      { type: "h4", text: "En dash" },
      {
        type: "ul",
        items: [
          "Use an en dash to join the lower and upper ends of a range of numbers, unless the range is preceded by “from” (in which case, use “to”).",
          'Use “–” with no spaces before or after rather than “-”, e.g. 5–13 or from 5 to 13.',
        ],
      },

      { type: "h4", text: "Oxford comma" },
      {
        type: "ul",
        items: [
          "“When a conjunction joins the last two elements in a series of three or more, a comma—known as the serial or series comma or the Oxford comma—should appear before the conjunction,”³ e.g. All I have left are hopes, dreams, and a mountain of unpaid bills.",
        ],
      },

      { type: "h4", text: "Quotation mark" },
      {
        type: "ul",
        items: [
          "Use double quotation marks when writing dialogue, quotations, or passages from someone else’s work.",
          "Use single quotation marks to represent a quote within a quote, e.g. “The man said to him, ‘Stop right there.’”",
        ],
      },

      { type: "h3", text: "Numbers" },
      {
        type: "ul",
        items: [
          "Spell out all numbers below 10, e.g. zero, three, seven.",
          "Use numerals for numbers 10 and higher, e.g. 22, 186, 593.",
          "Spell out numbers that start a sentence, e.g. Twenty-three years had passed since their last encounter.",
          "Large numbers should be spelled out, e.g. one million, thousands.",
        ],
      },

      { type: "h3", text: "Capitalization and casing" },
      {
        type: "p",
        text: "Proper capitalization of titles, headings, and certain words helps ensure readability and clarity.",
      },
      {
        type: "p",
        text: "Refer to the following table for some of the key rules to follow when formatting content:",
      },
      {
        type: "table",
        headers: ["Type", "Title Casing", "Sentence casing", "Capitalization", "ALL CAPS"],
        rows: [
          [
            "Description",
            "Article Titles",
            "H2/H3 subheadings",
            "Titles used with or in place of names\nRacial or ethnic groups\nSeasons (to avoid confusion)",
            "Acronyms",
          ],
          ["Example", "East of Eden", "Choose your partner", "Mr. President\nMother Jones\nBlack woman\nSpring or Fall", "COVID-19"],
        ],
      },
    ],
  },

  {
    title: "Formatting",
    blocks: [
      {
        type: "p",
        text: "To create content that is easy to read and understand, we recommend our writers use simple, straightforward formatting that breaks up the text and makes it more accessible to our readers.",
      },
      {
        type: "p",
        text: "We encourage writers and editors to use headings, subheadings, and bullet points to organize content and make it easier to navigate. We also recommend using short paragraphs and sentences to keep readers engaged and avoid overwhelming them with too much information.",
      },
      {
        type: "p",
        text: "When emphasizing important points, we suggest using bold or italics sparingly. These formatting styles can effectively draw attention to key takeaways or insights, but overusing them can distract or confuse the reader.",
      },
    ],
  },

  {
    title: "Images and multimedia",
    blocks: [
      { type: "p", text: "We encourage writers and editors to use images and multimedia to enhance the reader’s experience." },
      {
        type: "p",
        text: "Images and multimedia must be properly credited and cited to ensure we adhere to best practices. We encourage high-quality visuals relevant to the topic that help illustrate key points." },
      {
        type: "p",
        text: "It's important to note that all images and multimedia must be obtained through reputable sources, and we must have the legal right to use them. Therefore, we recommend using stock image sites or creating original visuals whenever possible." },
      {
        type: "p",
        text: "We must avoid using copyrighted, offensive, or inappropriate visuals."
      },
      {
        type: "p",
        label: "See also:",
        linkText: "Free licenced images",
        anchorTo: "sg-free-licenced-images",
      },
    ],
  },

  {
    title: "Search engine optimization (SEO)",
    blocks: [
      { type: "h3", text: "What is SEO?" },
      {
        type: "p",
        text: "Search engine optimization (SEO) is a powerful technique that helps web pages rank higher in search engine results pages (SERPs). The main goal of SEO is to increase organic traffic to a website by making it more visible to search engine users. By utilizing various strategies such as optimizing keyphrases, writing quality articles, and improving website structure, you can improve your article’s visibility and reach a wider audience.",
      },

      { type: "h3", text: "Why is SEO important for writers?" },
      {
        type: "p",
        text: "SEO is essential because it can help your articles reach a broader audience. By optimizing your articles for search engines, you can attract more readers who are actively searching for the topics you cover. This means that you're not just relying on social media or other forms of promotion to get your articles in front of people—you're actively targeting the people most likely to be interested in what you have to say.",
      },
      {
        type: "p",
        text: "Beyond that, SEO can also help you establish yourself as an authority in your niche. When you consistently write high-quality, SEO-friendly articles, you're sending a message to both readers and search engines that you know your stuff. This can lead to more opportunities for collaboration, speaking engagements, and other forms of professional recognition.",
      },
      {
        type: "p",
        text: "Incorporating SEO techniques into your writing can significantly impact your writing career. It can help you expand your readership, increase your visibility, and establish yourself as a thought leader in your field.",
      },
      {
        type: "p",
        text: "Here are some strategies to boost our content’s SEO authority and elevate rankings:",
      },
      { type: "ul", items: ["Write for humans: This means understanding your audience and writing to attract interest. You want other content creators to use your work as linked sources in their writings. By creating something natural and helpful, you earn points of credibility that increase the likelihood of a valuable link."] },
      { type: "ul", items: ["Long content is better: Panda, one of Google’s algorithms, places a higher value on content with more depth. The search engine perceives longer content as generally offering more context and helpful information than shorter content. There are exceptions to this rule. Sometimes, a shorter story makes the full point; attempting to write further would mean running on with repetitive and redundant points. Use your judgment to determine the best length of the story, and aim for longer content whenever possible."] },
      { type: "ul", items: ["Make every sentence unique: Google’s algorithm detects plagiarized content efficiently. Never directly copy another site’s particular phrasing when writing your content. You can have similarities and even use some of the same key points, but you need to write them in a unique style and tone. Otherwise, Google will penalize the content and the site where it’s published, lowering rankings and reducing organic traffic."] },
      { type: "ul", items: ["Include other assets where appropriate: Sometimes, a story may benefit from featuring video content about the subject. Embedding videos that expand the scope and relevance of the content is a great way to win brownie points from Google. Similarly, photos, charts, and infographics can tell a more succinct story and increase overall authority."] },
      {
        type: "p",
        label: "See also:",
        linkText: "How to write SEO-friendly articles",
        anchorTo: "sg-how-to-write-seo-friendly-articles",
      },
    ],
  },

  {
    title: "Approved and unapproved content",
    blocks: [
      {
        type: "p",
        text: "At FLoW, we aim to provide high-quality and meaningful content for our readers. We have established guidelines for approved and unapproved content to ensure consistency and maintain our standards.",
      },

      { type: "h3", text: "Approved content" },
      {
        type: "ul",
        items: [
          "Articles that are well-researched, accurate, and relevant to our readers.",
          "Original, previously unpublished content.",
          "Opinion pieces that are thought-provoking and backed by strong arguments.",
          "Content that is inclusive and respectful of all individuals, regardless of race, gender, religion, or sexual orientation.",
          "Content that aligns with our mission of inspiring, empowering, and uplifting our readers.",
        ],
      },

      { type: "h3", text: "Unapproved content" },
      {
        type: "ul",
        items: [
          "Content that is plagiarized or has been published elsewhere. Use proper citations when quoting other content or obtain permission for reprints.",
          "Content that promotes hate speech, discrimination, or violence.",
          "Content that infringes on someone's privacy or is defamatory or libellous.",
          "Content that is sexually explicit, pornographic, or obscene.",
          "Content that is intentionally misleading or inaccurate.",
          "Content that promotes illegal activities or substances.",
        ],
      },
      { type: "p", text: "We want to provide a safe and welcoming platform for our readers to engage with quality content." },
    ],
  },

  {
    title: "Plagiarism and AI-generated content",
    blocks: [
      {
        type: "p",
        text: "Maintaining the highest standards of integrity in all our writing is critical. All article submissions must be composed of original work only and will be verified using AI and plagiarism checkers.",
      },
      {
        type: "p",
        text: "Using the work of others, whether humans or Artificial Intelligence (AI), without proper attribution and the addition of original thought, is strictly prohibited. Violating this policy will result in immediate contract termination.",
      },
      {
        type: "p",
        text: "Note that some statements of fact may be viewed as plagiarism if unsupported by documentation. Either cite the source or reword to something less definitive, e.g. changing “this is true” to “this might be true.”",
      },

      { type: "h3", text: "Sources and citations" },
      {
        type: "p",
        text: "To ensure the credibility and accuracy of content, proper citation of any external sources using Canadian citation standards is crucial.",
      },
      {
        type: "p",
        text: "We encourage our writers to use reputable sources such as academic journals, government websites, and well-established news outlets. It is important to avoid using biased sources or those lacking credibility, as this can compromise the integrity of our publication.",
      },
      {
        type: "p",
        text: "When selecting sources, it is also important to consider the timeliness and relevance of the information. Our readers trust us to provide current and useful information, so we strive to use the most up-to-date available sources.",
      },
      { type: "h3", text: "Acceptable use of AI-generated content" },
      {
        type: "ul",
        items: [
          "Writers may reference AI-generated content during the brainstorming process for inspiration before writing.",
          "AI-generated content may be used to gather information during the research phase before writing.",
          "AI-generated information must always be cross-referenced with multiple reliable sources to ensure accuracy before writing.",
          "AI-generated content must never be used as a replacement for the writer's original work.",
          "AI-generated content must never be presented as the writer's original thoughts.",
        ],
      },
    ],
  },

  {
    title: "Appendices",
    blocks: [
      { type: "h3", text: "Article creation timeline" },
      { type: "p", text: "These are minimum standards. If you can move faster without sacrificing quality, you’re encouraged to do so." },
      {
        type: "ol",

        items: [
          {
            text: "Writer proposes or claims a topic: 1 day",
            children: [{ text: "Lead Editor assigns Editor" }],
          },
         { text: "Writer and Editor finalize topic: 2 days" },
          { text: "Writer submits 1st draft: 7 days*" },
          { text: "Editor submits 1st round of structural edits: 4 days" },
          { text: "Writer submits 2nd draft: 4 days" },
          { text: "Editor submits 2nd round of copy edits: 3 days" },
          { text: "Writer submits 3rd/final draft: 3 days" },
          { text: "Editor performs 3rd/final round of proofreading edits: 3 days" },
          {
            text: "Article publication: 3 days",
            children: [
              { text: "Editor uploads article to website" },
              { text: "Lead Editor reviews article and schedules for publication" },
            ],
          },
        ],
      },
      {
        type: "p",
        text: "* Writer proposes or claims their next topic any time after submitting their first draft.",
      },
      {
        type: "p",
        text: "Articles shorter than 2,500 words should be completed on a shorter timeline:",
      },
      {
        type: "ul",
        items: [
          "2,500+ word article = 30 days",
          "2,000–2,499 words = 28 days",
          "1,500–1,999 words = 26 days",
          "1,000–1,499 words = 24 days",
          "500–999 words = 22 days",
        ],
      },

      { type: "h3", text: "Publication checklist for writers" },

      // Bold subheading
      { type: "p", text: "Before you send in your first draft, discuss it with your Editor:", bold: true },

      // Checkbox list (with a nested checkbox list under Brainstorm)
      {
        type: "checklist",
        items: [
          {
            text: "Brainstorm ideas for articles you want to write.",
            children: [
              { text: "What are you passionate about?" },
              { text: "What do you need to tell the world?" },
              { text: "Why does the world need to hear this?" },
            ],
            childrenType: "checklist",
          },
          { text: "Submit 3–5 topics to your Editor and workshop them together." },
          {
            text: "Choose one topic and write an outline, including any sources you might use, and send it to your Editor.",
          },
          {
            text: "Wait for your Editor to provide feedback on your outline and give the go-ahead before you start drafting the article.",
          },
        ],
      },

      // Bold subheading
      { type: "p", text: "Sending the first draft in Google Docs:", bold: true },

      // Checkbox list
      {
        type: "checklist",
        items: [
          { text: "Save your article in Google Docs." },
          { text: "Run a spellcheck in Google Docs." },
          { text: "Run your content through AI and plagiarism detectors and address any red flags." },
          { text: "Don’t worry too much about formatting at this stage." },
          { text: "Share your article with your Editor via Google Docs." },
          { text: "Ensure your Editor has full access to edit your article in Google Docs." },
          {
            text:
              "Include one image per 500 words. Images must not be too large (300 kB or less) or too small.",
          },
          {
            text:
              "Unless you created the images, include the website where you retrieved them to ensure commercial licencing for copyright purposes.",
          },
          {
            text:
              "Include at least three links per 500 words. These are added value for the reader so they can find more information on topics mentioned in your article. Choose the anchor text that the link should be attached to. Make sure they are high-quality links. Is it an article you would find interesting to read?",
          },
        ],
      },

      // Bold subheading
      { type: "p", text: "Checking edits in track changes in Google Docs:", bold: true },

      {
        type: "checklist",
        items: [
          { text: "Once your Editor has performed their edits, accept all changes you agree with in Google Docs." },
          { text: "Read comments in the margins for any additional recommendations." },
          { text: "If you have any questions or want to discuss any edits or suggestions, contact your Editor." },
          {
            text:
              "Review the article to ensure it is clean and ready for the next editing round or publication, including all images and hyperlinks.",
          },
          {
            text:
              "This process can go back and forth several times until you and the Editor are both happy with the article and agree that it is ready for publication.",
          },
        ],
      },

      // Bold subheading
      { type: "p", text: "Checklist before sending in for publication:", bold: true },

      {
        type: "checklist",
        items: [
          { text: "One last proofread for omissions and typos." },
          { text: "Run a spellcheck." },
          { text: "Run your content through AI and plagiarism detectors and address any red flags." },
          { text: "Include all hyperlinks anchored to the text." },
          { text: "Include all images in the text or as attachments, along with the links where you found them." },
          { text: "Suggest 3–5 keyphrases for tagging on the website." },
          { text: "If this is your first article or you need to make changes, send your photo and bio to your Editor." },
          { text: "Tell your Editor when you are satisfied with the final draft and it’s ready for publication." },
        ],
      },

      // Bold subheading
      { type: "p", text: "Once the article is published:", bold: true },

      {
        type: "checklist",
        items: [{ text: "Share your article on social media!" }],
      },

      { type: "h3", text: "Free licenced images", id: "sg-free-licenced-images" },

      // Pexels
      {
        type: "h4",
        text: "Pexels",
      },
      {
        type: "p",
        text: "https://www.pexels.com/",
        link: true,
      },
      {
        type: "p",
        text: "Licence: All photos and videos on Pexels can be downloaded and used for free.",
        boldLabel: "Licence:",
      },
      { type: "p", text: "What is allowed:", bold: true },
      {
        type: "ul",
        items: [
          "Attribution is not required. Giving credit to the photographer or Pexels is unnecessary but always appreciated.",
          "You can modify photos and videos from Pexels. Be creative and edit them as you like.",
        ],
      },
      { type: "p", text: "What is not allowed:", bold: true },
      {
        type: "ul",
        items: [
          "Identifiable people may not appear in a bad light or in a way that is offensive.",
          "Don't sell unaltered copies as posters, prints, or other physical products.",
          "Don't imply endorsement of your product by people or brands in the imagery.",
          "Don't redistribute or sell the photos and videos on other stock photo or wallpaper platforms.",
        ],
      },

      // PxHere
      {
        type: "h4",
        text: "PxHere",
      },
      {
        type: "p",
        text: "https://pxhere.com/",
        link: true,
      },
      {
        type: "p",
        text:
          "Licence: All photos on PxHere are licenced under the Creative Commons Zero (CC0) licence.",
        boldLabel: "Licence:",
      },
      { type: "p", text: "What is allowed:", bold: true },
      {
        type: "ul",
        items: [
          "The pictures are free to use for any legal purpose.",
          "The pictures are free for personal and commercial use.",
          "You can modify, copy, and distribute the photos without asking for permission or setting a link to the source; attribution is not required.",
        ],
      },
      { type: "p", text: "What is not allowed:", bold: true },
      {
        type: "ul",
        items: [
          "Identifiable people may not appear in a bad light or in a way they may find offensive without their consent.",
        ],
      },

      // Pixabay
      {
        type: "h4",
        text: "Pixabay",
      },
      {
        type: "p",
        text: "https://pixabay.com/",
        link: true,
      },
      {
        type: "p",
        text: "Licence: The Pixabay licence empowers creators and protects our community.",
        boldLabel: "Licence:",
      },
      { type: "p", text: "What is allowed:", bold: true },
      {
        type: "ul",
        items: [
          'All content—images, videos, music—can be used for commercial and noncommercial use across print and digital, except in the cases mentioned in "What is not allowed."',
          "Attribution is not required.",
          "You can make modifications to the content.",
        ],
      },
      { type: "p", text: "What is not allowed:", bold: true },
      {
        type: "ul",
        items: [
          "Don't redistribute or sell Pixabay content on other stock or wallpaper platforms.",
          "Don't sell unaltered copies of content as posters, prints, or other physical products.",
          "Don't portray identifiable people in a bad light or in a way that is offensive.",
          "Don't use content with identifiable persons or brands to create a misleading association with a product or service.",
        ],
      },

      // Unsplash
      {
        type: "h4",
        text: "Unsplash",
      },
      {
        type: "p",
        text: "https://unsplash.com/",
        link: true,
      },
      {
        type: "p",
        text:
          "Licence: Unsplash grants users an irrevocable, nonexclusive, worldwide copyright licence to download, copy, modify, distribute, perform, and use photos from their platform.",
        boldLabel: "Licence:",
      },
      { type: "p", text: "What is allowed:", bold: true },
      {
        type: "ul",
        items: [
          "All photos can be downloaded and used for free.",
          "Commercial and non-commercial purposes are allowed.",
          "No permission is needed, although attribution is appreciated.",
        ],
      },
      { type: "p", text: "What is not allowed:", bold: true },
      {
        type: "ul",
        items: [
          "Selling Unsplash photos without significant modification.",
          "Compiling photos from Unsplash to replicate a similar or competing service.",
        ],
      },

      { type: "h3", text: "Other external resources" },

      {
        type: "kv",
        rows: [
          { key: "AI detector", values: ["Grammarly", "QuillBot"] },
          { key: "Citations", values: ["The Chicago Manual of Style, 17th ed. (2017)"] },
          { key: "Grammar", values: ["The Chicago Manual of Style, 17th ed. (2017)"] },
          { key: "Numbers", values: ["The Chicago Manual of Style, 17th ed. (2017)"] },
          { key: "Plagiarism detector", values: ["Grammarly", "QuillBot"] },
          { key: "Punctuation", values: ["The Chicago Manual of Style, 17th ed. (2017)"] },
          { key: "Spelling", values: ["Canadian Oxford Dictionary", "Termium Plus"] },
          { key: "Style", values: ["The Chicago Manual of Style, 17th ed. (2017)"] },
        ],
      },

      { type: "h3", text: "WordPress user guide" },
      {
        type: "p",
        text: "NOTE: While we transition to our new website, editors will continue to use WordPress during the final editing steps to take advantage of built-in SEO and readability tools. Once the final review is completed, content will be published on the new website.",
      },
      {
        type: "ol",
        items: [
          {
            text: "Access the WordPress dashboard (not currently available for writers):",
            children: [
              { text: "Open your preferred web browser and go to FLoW’s WordPress URL: https://forloveofwriters.com/wp-admin" },
              { text: "Enter your Username and Password to log in to the WordPress dashboard." },
            ],
          },
          {
            text: "Create a new post:",
            children: [
              { text: 'On the dashboard, click "Posts" in the left-hand sidebar and select "Add New."' },
              { text: "A new post editor screen will appear." },
              { text: "Add the title, main text, and links." },
              { text: "Use the formatting tools provided to style your content, e.g. bold, italic, underline, and bullet points." },
              { text: 'Add images or videos by clicking the "Add Media" button.' },
            ],
          },
          {
            text: "Save a post:",
            children: [{ text: 'Click the "Save Draft" button on the right-hand side.' }],
          },
          {
            text: "View and edit posts:",
            children: [
              { text: 'Go to the "Posts" section in the dashboard and select "All Posts."' },
              { text: "You will see a list of all your posts and their current status." },
              { text: "Edit a post by clicking on its title to open the post editor." },
            ],
          },
          {
            text: "Add categories and tags:",
            children: [
              { text: 'Select a category from the "Categories" section on the right-hand side of the post editor.' },
              { text: 'You can create new categories by clicking the "Add New Category" link.' },
              { text: 'Type tags into the "Tags" field in the right-hand sidebar.' },
            ],
          },
          {
            text: "Manage your profile:",
            children: [
              { text: 'Click "Users" in the left-hand sidebar and select "Your Profile."' },
              { text: "Add or edit your display name, contact information, and password." },
              { text: 'Add or edit your profile picture by uploading an image in the "Avatar" section.' },
            ],
          },
        ],
      },

      { type: "h3", text: "How to write SEO-friendly articles", id: "sg-how-to-write-seo-friendly-articles" },
      {
        type: "ol",
        items: [
          {
            text: "Choose a focus keyphrase:",
            children: [
              { text: "Start by choosing a focus keyphrase for your article." },
              { text: "This should be a term that accurately reflects the main topic of your article." },
            ],
          },
          {
            text: "Optimize your title:",
            children: [
              { text: "Your title is the first thing that readers will see in search engine results, so it’s essential to optimize it to maximize the number of readers who find your article." },
              { text: "Include your focus keyphrase in your title, and keep it under the recommended 43-character limit to avoid having the end trimmed off by search engines." },
            ],
          },
          {
            text: "Optimize your meta description:",
            children: [
              { text: "Your meta description is the short blurb below your headline in search engine results." },
              { text: "It should be compelling and give readers a clear idea of your article topic." },
              { text: "The meta description should include your focus keyphrase and be under the recommended 156-character limit to avoid having the end trimmed off by search engines." },
            ],
          },
          {
            text: "Use your focus keyphrase throughout your article:",
            children: [
              { text: "Aim to include your keyphrase in your introduction, subheadings, and body text while keeping your writing natural and engaging." },
            ],
          },
          {
            text: "Use header tags:",
            children: [
              { text: "Use header tags (H1, H2, H3, etc.) to structure your article and make it easy for readers to scan." },
              { text: "Include your focus keyphrase in at least one of your header tags." },
            ],
          },
          {
            text: "Use internal and external links:",
            children: [
              { text: "Including links to other pages on your website (internal linking) and to other relevant sources outside your website (external linking) can help establish the credibility of your article and improve its visibility in search engine results." },
              { text: "Use descriptive anchor text that includes your focus keyphrase." },
            ],
          },
          {
            text: "Optimize your images:",
            children: [
              { text: "Images can add visual interest to your article and improve its readability, but they can also slow down your page load times unless optimized." },
              { text: "Use alt tags and descriptive file names that include your primary keyphrase." },
            ],
          },
          {
            text: "Write high-quality articles:",
            children: [
              { text: "The most important thing you can do to improve your SEO is to write high-quality articles that provide value to your readers." },
              { text: "Make sure your article is well-researched, informative, and engaging." },
            ],
          },
        ],
      },

      { type: "h3", text: "Yoast plugin user guide" },
      {
        type: "p",
        text: "Yoast is a popular plugin that helps optimize website content for search engines. It offers a range of features including keyphrase analysis, content analysis, and more. You are encouraged to incorporate as many of the optimization suggestions as possible.",
      },
      {
        type: "ol",
        items: [
          {
            text: "Set a focus keyphrase:",
            children: [
              { text: "Open the Yoast SEO section of the post editor." },
              { text: "Enter the focus keyphrase that you want your article to rank for in search engine results." },
              { text: "This keyphrase should be relevant to your content and ideally included in the title." }
            ]
          },

          {
            text: "Optimize your title:",
            children: [
              { text: "The title of your post is one of the most important factors in SEO and should be optimized to include your focus keyphrase and be attention-grabbing for readers." },
              { text: "Yoast will analyze your title and provide optimization suggestions for search engines and readers." }
            ]
          },

          {
            text: "Optimize your meta description:",
            children: [
              { text: "The meta description is the short description that appears under the title of each post in search engine results and should be optimized to include your focus keyphrase and be compelling for readers to click through and view." },
              { text: "Yoast will analyze your meta description and suggest how to optimize it." }
            ]
          },

          {
            text: "Optimize your content:",
            children: [
              { text: "Yoast analyzes the contents of your post and suggests ways to optimize it for your focus keyphrase." },
              { text: "This includes optimizing your headings, images, and other elements of your post." }
            ]
          },

          {
            text: "Check your readability score:",
            children: [
              { text: 'Click the "Content Analysis" tab.' },
              { text: 'Here you will see a "Readability" score and tips for improving your article\'s readability, such as shorter sentences, shorter paragraphs, and simpler words.' }
            ]
          },

          {
            text: "Use internal and external links:",
            children: [
              { text: "Linking to other relevant content on your website (internal linking) and other authoritative websites (external linking) can help improve your SEO." },
              { text: "Use internal and external links where appropriate in your post." }
            ]
          },

          {
            text: "Use schema markup:",
            children: [
              { text: "Schema markup is a type of microdata that helps search engines better understand the content of your post." },
              { text: "Use schema markup where appropriate in your post." }
            ]
          },

          {
            text: "Use optimized images:",
            children: [
              { text: "Optimized images can help improve your post's SEO." },
              { text: "Use images that are relevant to your post and optimized with alt text and proper file names." }
            ]
          },

          {
            text: "Check for errors:",
            children: [
              { text: "Before publishing your post, use Yoast's SEO analysis tool to check for issues and correct them." }
            ]
          },
        ],
      },
      {
        type: "p",
        text: "You can use these basic SEO strategies and techniques with the Yoast plugin to optimize your post for search engines. Remember, SEO is an ongoing process, so keep monitoring and updating your post to improve its SEO over time.",
      },
    ],
  },

  {
    title: "Footnotes",
    blocks: [
      { type: "p", text: "¹ The Chicago Manual of Style 16th Edition, 7.19" },
      { type: "p", text: "² The Chicago Manual of Style 16th Edition, 6.82" },
      { type: "p", text: "³ The Chicago Manual of Style 16th Edition, 6.18" },
    ],
  },
];
